import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router';
import {
  LayoutDashboard, Users, FileText, ShieldCheck,
  Receipt, ClipboardList, Scissors, FilePen, Settings, UserCog, LogOut,
  Menu, X, Pill, ClipboardCheck, BedDouble, Megaphone,
  FileSignature, CalendarClock, CalendarRange,
  Clock, Syringe, Sun, Moon, History, HardDrive, MessageCircle, Undo2, TrendingUp,
  Mail, Banknote, BookOpen, CreditCard, UserPlus, MessagesSquare, Target,
  ShieldPlus, FlaskConical, Lightbulb, ListTodo,
} from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
// Quién tiene el Agente (D7, 22/9/2026): `tieneAgente`, la misma raya que el
// servidor. Antes aquí solo se miraba `doctor`, y al anestesista le salían la
// campana y el botón del Agente que el servidor le niega.
import { isManagement, isAdmin, isDoctor, isAnestesista, roleLabel, normalizeRole, tieneAgente, tieneChat, tieneMejorasSoftware, veDrive, veLeads } from '../lib/roles';
// El globo del chat interno (23/9/2026): los mensajes sin leer de las salas
// de esta persona, preguntados cada 30 s. Lo lleva AppShell una sola vez y se
// lo pasa al menú, que se pinta dos veces (lateral y el del móvil).
import { useChatSinLeer } from '../lib/chatSinLeer';
// Y el de los leads nuevos (28/9/2026): lo que ha entrado desde la última vez
// que esta persona abrió Leads, con la campanita y un aviso cuando sube.
import { useLeadsNuevos } from '../lib/leadsNuevos';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import ChangePasswordGuard from './ChangePasswordGuard';
import CommandPalette, { useCommandPaletteShortcut } from './CommandPalette';
import AvisosBell from './AvisosBell';
import SonidosVigilante from './SonidosVigilante';
import { sonarPitido } from '../lib/sonidos';
import AgenteAskDialog from './AgenteAskDialog';
import AgenteIcono from './AgenteIcono';
// La grabación de consulta en marcha, en todas las pantallas (26/9/2026).
import BarraGrabacion from './BarraGrabacion';
import { gestorGrabacion } from '../lib/grabadoraConsulta';

// El icono premium del agente también en el menú (unificado, 2026-08-24).
// Recibe el className del renderizador de items y lo ignora a propósito:
// la moneda lleva su propio color.
import { getTheme, toggleTheme } from '../lib/theme';
import { useClinica, pieClinica } from '../lib/clinica';
import { applyDocumentTitle } from '../lib/clinicProfile';

/** Selector claro/oscuro (arriba a la izquierda). Por defecto claro. */
function ThemeToggle() {
  const [theme, setThemeState] = useState(() => getTheme());
  const onToggle = () => setThemeState(toggleTheme());
  const dark = theme === 'dark';
  return (
    <button
      type="button"
      onClick={onToggle}
      title={dark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
      aria-label="Cambiar tema claro/oscuro"
      data-testid="theme-toggle"
      className="inline-flex items-center justify-center w-7 h-7 rounded-md text-muted-foreground hover:text-[#2E6E5E] hover:bg-[rgba(46,110,94,0.08)] transition-colors shrink-0"
    >
      {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
    </button>
  );
}
import { ClinicPilotBadge, ClinicPilotMark, ClinicPilotLogo } from './ClinicPilotBrand';

const NAV_GROUPS = [
  {
    label: 'General',
    items: [
      { to: '/', icon: LayoutDashboard, label: 'Dashboard', testid: 'sidebar-nav-dashboard-link', end: true },
      // Chat interno (23/9/2026): administración, dirección y enfermería
      // (28/9/2026), como su ruta (`SoloChat`). Qué salas ve cada uno lo
      // decide el servidor: a enfermería, solo la global.
      { to: '/chat', icon: MessagesSquare, label: 'Chat', testid: 'sidebar-nav-chat-link', chatOnly: true },
      { to: '/citas', icon: CalendarClock, label: 'Agenda', testid: 'sidebar-nav-citas-link', doctorAllowed: true },
      { to: '/quirofano-sesiones', icon: CalendarRange, label: 'Quirófano', testid: 'sidebar-nav-quirofano-sesiones-link', anestesistaAllowed: true, doctorAllowed: true },
      { to: '/registro-jornada', icon: Clock, label: 'Registro de jornada', testid: 'sidebar-nav-jornada-link' },
    ],
  },
  {
    label: 'Pacientes',
    items: [
      // Quien ha preguntado y aún no es paciente (2026-09-22): gestión y, desde
      // el 22/10/2026, quien tiene campañas elegidas en Usuarios («Leads: solo
      // de estas campañas»), aunque su rol no entre (`veLeads`).
      { to: '/leads', icon: UserPlus, label: 'Leads / Contactos', testid: 'sidebar-nav-leads-link', leadsOnly: true },
      { to: '/pacientes', icon: Users, label: 'Pacientes', testid: 'sidebar-nav-patients-link' },
      // Seguimientos (administración, 4/11/2026): los pacientes en seguimiento
      // y los completados. De gestión, como su ruta (`SoloGestion`) y su
      // servidor (GET /api/seguimientos).
      { to: '/seguimientos', icon: ListTodo, label: 'Seguimientos', testid: 'sidebar-nav-seguimientos-link', gestionOnly: true },
      { to: '/presupuestos', icon: FileText, label: 'Presupuestos', testid: 'sidebar-nav-budgets-link', gestionOnly: true },  // solo administración: el importe es confidencial (2026-08-18)
      { to: '/lopd', icon: ShieldCheck, label: 'LOPD', testid: 'sidebar-nav-lopd-link' },
      // Fuera del menú «Pruebas preop.» e «Indicaciones cirugía» (Antonio,
      // 19/10/2026: casi sin uso; las dos se generan solas). Las pantallas
      // siguen: se llega desde sus tarjetas en la ficha del paciente.
      { to: '/recetas', icon: Pill, label: 'Recetas', testid: 'sidebar-nav-recipes-link', doctorAllowed: true, anestesistaAllowed: true },
      { to: '/postoperatorios', icon: BedDouble, label: 'Postoperatorios', testid: 'sidebar-nav-postop-link' },
      { to: '/justificantes', icon: ClipboardCheck, label: 'Justificantes', testid: 'sidebar-nav-justifications-link' },
      { to: '/informes', icon: FileSignature, label: 'Informes', testid: 'sidebar-nav-informes-link', doctorAllowed: true },
      { to: '/consentimientos', icon: FilePen, label: 'Consentimientos', testid: 'sidebar-nav-consents-link' },
    ],
  },
  {
    // El grupo ya NO es solo de dirección: dentro hay algo (Devoluciones) que
    // llevan las administrativas. Cada entrada mantiene su propio permiso, así
    // que Cobros e Indicadores siguen igual de cerrados.
    label: 'Gestión',
    items: [
      // Las administrativas entran para cuadrar LA CAJA DEL DÍA (dirección,
      // 2026-09-17: «pueden ver caja del día pero no KPIs ni del mes»). Hasta
      // hoy ni veían la entrada, y a la vez sí podían apuntar cobros y
      // devoluciones: movían un número que no les dejábamos mirar. Dentro, la
      // pantalla les da un día y el servidor les niega el mes, el histórico y
      // el Excel. Enfermería y los clínicos siguen fuera (`gestionOnly`).
      { to: '/cobros', icon: Receipt, label: 'Cobros', testid: 'sidebar-nav-cobros-link', gestionOnly: true },
      { to: '/datafono', icon: CreditCard, label: 'Datáfono', testid: 'sidebar-nav-datafono-link', gestionOnly: true },  // anotar pagos con tarjeta (2026-08-19)
      // Solo dirección y administración (administradores): aquí se ven importes
      // (dirección, 2026-09-08, punto 6). El backend lo cierra igual.
      { to: '/registro-externos', icon: Clock, label: 'Registro externos', testid: 'sidebar-nav-registro-externos-link', adminOnly: true },
      // Lo llevan las administrativas: son las que se enteran de que hay que
      // devolver algo o de que ya se ha devuelto (2026-07-28).
      { to: '/devoluciones', icon: Undo2, label: 'Facturas y devoluciones', testid: 'sidebar-nav-devoluciones-link', gestionOnly: true },
      // Facturación por médico colaborador (2026-08-25): gestión ve a todos;
      // los doctores entran y ven/registran lo suyo.
      { to: '/colaboradores', icon: Banknote, label: 'Colaboradores', testid: 'sidebar-nav-colaboradores-link', colaboradores: true },
      { to: '/indicadores', icon: TrendingUp, label: 'Indicadores', testid: 'sidebar-nav-indicadores-link', adminOnly: true },
      // Las campañas de publicidad, lo que vale cada una y lo que trae
      // (27/9/2026): solo administradores, como su ruta (`SoloAdmin`) y el
      // servidor (`require_role("admin")`).
      { to: '/campanas', icon: Target, label: 'Campañas', testid: 'sidebar-nav-campanas-link', adminOnly: true },
    ],
  },
  {
    label: 'Quirófano',
    items: [
      { to: '/anestesia', icon: Syringe, label: 'Agenda de anestesia', testid: 'sidebar-nav-anestesia-link', anestesistaAllowed: true },
      { to: '/registro-quirurgico', icon: ClipboardList, label: 'Registro QX', testid: 'sidebar-nav-surgery-log-link', anestesistaAllowed: true },
      // Control de esterilización y de esporas (administración, 5/10/2026):
      // para los mismos que las dos de arriba (gestión, enfermería y
      // anestesia; ni doctores), como su ruta y `require_quirofano`.
      { to: '/control-esterilizacion', icon: ShieldPlus, label: 'Control de esterilización', testid: 'sidebar-nav-control-esterilizacion-link', anestesistaAllowed: true },
      { to: '/control-esporas', icon: FlaskConical, label: 'Control de esporas', testid: 'sidebar-nav-control-esporas-link', anestesistaAllowed: true },
    ],
  },
  {
    label: 'Administración',
    items: [
      // «Tareas dirección» y «Agente IA» salen del menú (dirección,
      // 23/9/2026): Tareas dirección sigue en /tareas-direccion; y el Agente
      // ya está arriba, en «¿Qué quieres hacer?», así que en el menú era
      // redundante. «Mejoras software» se quitó entera ese día y VUELVE el
      // 20/10/2026 con otro diseño (abajo): solo con su casilla de Usuarios.
      // Las pautas de las reuniones, por áreas. Las escribe dirección; las ve
      // todo el mundo, que de nada sirve un protocolo que no lee quien lo aplica.
      // Ni doctores ni anestesistas: los protocolos son de la casa (secretaria,
      // quirofano, enfermeria) y a ellos no les toca ninguno (2026-07-28).
      { to: '/protocolos', icon: BookOpen, label: 'Protocolos', testid: 'sidebar-nav-protocolos-link' },
      // Renombrado el 6/10/2026 (administración): además de las cirugías
      // lleva los tratamientos y las tarifas de los hospitales. Misma ruta.
      { to: '/catalogo-cirugias', icon: Scissors, label: 'Catálogo de cirugías, tratamientos y otros costes', testid: 'sidebar-nav-surgery-catalog-link' },
      // El Drive es del admin y, desde el 27/10/2026, de quien tiene carpetas
      // elegidas en Usuarios («Drive: entra en estas carpetas») o, desde el
      // 3/11/2026, la casilla «Drive: todo», aunque su rol no entre (`veDrive`).
      { to: '/drive', icon: HardDrive, label: 'ClinicPilot Drive', testid: 'sidebar-nav-drive-link', driveOnly: true },
      { to: '/whatsapp', icon: MessageCircle, label: 'WhatsApp', testid: 'sidebar-nav-whatsapp-link', gestionOnly: true },
      { to: '/mail', icon: Mail, label: 'Mail', testid: 'sidebar-nav-mail-link', gestionOnly: true },
      { to: '/usuarios', icon: UserCog, label: 'Usuarios', testid: 'sidebar-nav-users-link', adminOnly: true },
      { to: '/control-rastreo', icon: History, label: 'Control rastreo', testid: 'sidebar-nav-rastreo-link', adminOnly: true },
      // Mejoras del software (20/10/2026): se escribe una mejora y la hace una
      // rutina automática. Solo con la casilla «Mejoras software» de Usuarios,
      // sea cual sea el rol: un admin sin ella no la ve (`tieneMejorasSoftware`,
      // la misma regla que el servidor).
      { to: '/mejoras-software', icon: Lightbulb, label: 'Mejoras software', testid: 'sidebar-nav-mejoras-link', mejorasOnly: true },
      { to: '/marketing', icon: Megaphone, label: 'Marketing', testid: 'sidebar-nav-marketing-link', adminOnly: true },
      { to: '/configuracion', icon: Settings, label: 'Configuración', testid: 'sidebar-nav-settings-link' },
    ],
  },
];

function SidebarContent({ onItemClick, onSearchClick, chatSinLeer = 0, leadsNuevos = 0 }) {
  const { user, logout } = useAuth();
  const clinicaDatos = useClinica();
  // Pautas de protocolo que le quedan por confirmar a esta persona. Se
  // refresca al cambiar de pantalla, que es cuando puede haber cambiado.
  const location = useLocation();
  const [protocolosPendientes, setProtocolosPendientes] = useState(0);
  const [protocoloCartel, setProtocoloCartel] = useState(null);
  // Aviso sonoro al conectarse/aparecer una pauta nueva por confirmar
  // (2026-08-27, dirección). Suena UNA vez por cada «sello» nuevo.
  const selloProtocoloRef = useRef(null);
  useEffect(() => {
    let vivo = true;
    api.get('/api/protocolos/pendientes')
      .then((r) => {
        if (!vivo) return;
        const d = r.data || {};
        setProtocolosPendientes(d.total || 0);
        setProtocoloCartel(d.total > 0 ? (d.cartel || 'Protocolo por leer') : null);
        if (d.sello && d.sello !== selloProtocoloRef.current) {
          // No sonar en la PRIMERA carga de la sesión si ya se había mostrado
          // antes; solo cuando aparece algo nuevo respecto a lo último visto.
          if (selloProtocoloRef.current !== null) {
            try { sonarPitido(); } catch { /* audio no disponible */ }
          }
          selloProtocoloRef.current = d.sello;
        } else if (!d.sello) {
          selloProtocoloRef.current = null;
        }
      })
      .catch(() => {});
    return () => { vivo = false; };
  }, [location.pathname]);
  const mgmt = isManagement(user);
  const admin = isAdmin(user);
  const doctor = isDoctor(user);
  const anestesista = isAnestesista(user);
  const agente = tieneAgente(user);
  const chat = tieneChat(user);
  const mejoras = tieneMejorasSoftware(user);
  const leads = veLeads(user);
  const drive = veDrive(user);
  // Las mejoras propias que esperan una aclaración (20/10/2026): el globo de
  // «Mejoras software», que se mira al cambiar de pantalla, como los
  // protocolos. Solo a quien tiene la casilla (al resto, el servidor: 403).
  const [mejorasPorAclarar, setMejorasPorAclarar] = useState(0);
  useEffect(() => {
    if (!mejoras) { setMejorasPorAclarar(0); return undefined; }
    let vivo = true;
    api.get('/api/mejoras-software/aviso')
      .then((r) => { if (vivo) setMejorasPorAclarar(r.data?.necesitan_aclaracion || 0); })
      .catch(() => {});
    return () => { vivo = false; };
  }, [mejoras, location.pathname]);
  // ClinicPilot Drive: administradores, dirección y administrativos.
  const gestion = ['admin', 'direccion', 'administrativo'].includes(normalizeRole(user?.role));

  // Memoise the visible nav structure so we don't recompute the filter chain
  // on every render (and only when the user role flags actually change).
  const visibleGroups = useMemo(() => {
    return NAV_GROUPS
      .filter((g) => !g.managementOnly || mgmt)
      .map((group) => ({
        ...group,
        items: group.items.filter((i) => {
          // Por persona, no por rol (20/10/2026): con la casilla sale aunque
          // el rol no viera nada más de Administración; sin ella, a nadie.
          if (i.mejorasOnly) return mejoras;
          // Leads (22/10/2026): por la regla de `veLeads`, también a una
          // doctora con campañas elegidas, aunque no vea nada más de aquí.
          if (i.leadsOnly) return leads;
          // Drive (27/10/2026): por la regla de `veDrive`, también a quien
          // tiene carpetas elegidas, aunque no vea nada más de aquí.
          if (i.driveOnly) return drive;
          if (i.adminOnly && !admin) return false;
          if (i.managementOnly && !mgmt) return false;
          if (i.colaboradores) return gestion || doctor;
          if (i.gestionOnly && !gestion) return false;
          if (i.chatOnly && !chat) return false;
          // Phase 27 — los doctores solo ven su subconjunto de navegación.
          if (doctor && !i.doctorAllowed) return false;
          // 2026-07-24 — los anestesistas solo ven Quirófano y Anestesia.
          if (anestesista && !i.anestesistaAllowed) return false;
          return true;
        }),
      }))
      .filter((group) => group.items.length > 0);
  }, [mgmt, admin, doctor, gestion, anestesista, chat, mejoras, leads, drive]);

  // El platform-specific hint: ⌘ en Mac, Ctrl en Win/Linux.
  const isMac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform || '');
  const shortcutLabel = isMac ? '⌘K' : 'Ctrl+K';

  return (
    <div className="flex h-full w-full flex-col bg-white dark:bg-card" data-testid="app-sidebar">
      <div className="px-5 pt-5 pb-4 border-b border-border flex items-center gap-3">
        {/* El entorno es ClinicPilot; la marca de la clínica vive solo en los
            documentos que se emiten (informes, presupuestos, PDFs). */}
        <ClinicPilotLogo height={28} />
        {/* Selector de tema a la izquierda (no en la esquina derecha, donde la
            ✕ del menú móvil lo tapaba). */}
        <ThemeToggle />
        {/* Campana de avisos del Agente (solo administradores). Oculta en la
            Sheet móvil: ahí chocaba con la ✕ de cerrar, y el móvil ya tiene
            su campana en la barra superior. */}
        {/* La campana es de quien tiene el Agente —el correo y las tareas
            propias—: ni doctores ni anestesistas (D7, 22/9/2026). Dirección la
            conserva: su rol es admin, no doctor. */}
        {agente && <span className="ml-auto hidden lg:block"><AvisosBell /></span>}
      </div>

      {/* Al pulsar aquí se abre el AGENTE en el centro (2026-07-27): escribes
          y él busca y hace. Antes salía el menú de resultados del buscador, que
          ahora queda solo en cmd+K para quien prefiera navegar tecleando. */}
      {agente && (
      <button
        type="button"
        onClick={() => { if (onSearchClick) onSearchClick(); if (onItemClick) onItemClick(); }}
        className="mx-3 mt-3 mb-1 flex items-center gap-2 rounded-lg border border-border bg-[rgba(46,110,94,0.04)] px-3 py-2 text-sm text-muted-foreground hover:bg-[rgba(46,110,94,0.10)] hover:text-[#13231E] transition-colors"
        data-testid="sidebar-search-button"
      >
        <AgenteIcono size={18} />
        <span className="flex-1 text-left">¿Qué quieres hacer?</span>
        <kbd className="hidden lg:inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-border bg-white text-[10px] font-mono text-[#13231E]">
          {shortcutLabel}
        </kbd>
      </button>
      )}

      <nav className="flex-1 overflow-y-auto py-3">
        {visibleGroups.map((group) => (
            <div key={group.label} className="px-3 mb-4">
              <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{group.label}</div>
              <div className="space-y-1">
                {group.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    data-testid={item.testid}
                    onClick={() => onItemClick && onItemClick()}
                    className={({ isActive }) => `
                      flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors
                      ${isActive
                        ? 'nav-activo text-[#13231E] font-medium'
                        : 'text-muted-foreground hover:bg-[rgba(46,110,94,0.06)] hover:text-[#13231E]'}
                    `}
                  >
                    {/* Nombres largos (6/10/2026, «Catálogo de cirugías,
                        tratamientos y otros costes»): el texto parte en
                        varias líneas dentro del menú y el icono no encoge
                        (`shrink-0`); arriba, con la primera línea. En las de
                        una línea queda igual de centrado que antes. */}
                    <item.icon className="w-4 h-4 shrink-0 self-start mt-0.5 text-[#2E6E5E]" />
                    <span className="min-w-0 break-words">{item.label}</span>
                    {/* Mensajes del chat sin leer, sumando las salas (23/9/2026). */}
                    {item.to === '/chat' && chatSinLeer > 0 && (
                      <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-red-600 text-white text-[10px] font-bold"
                        data-testid="sidebar-chat-badge" title={`${chatSinLeer} mensajes sin leer`}>
                        {chatSinLeer > 99 ? '99+' : chatSinLeer}
                      </span>
                    )}
                    {/* Leads entrados desde la última vez que se abrió Leads
                        (28/9/2026), igual que el del chat. */}
                    {item.to === '/leads' && leadsNuevos > 0 && (
                      <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-red-600 text-white text-[10px] font-bold"
                        data-testid="sidebar-leads-badge" title={leadsNuevos === 1 ? '1 lead nuevo' : `${leadsNuevos} leads nuevos`}>
                        {leadsNuevos > 99 ? '99+' : leadsNuevos}
                      </span>
                    )}
                    {/* Mejoras propias que esperan una aclaración (20/10/2026). */}
                    {item.to === '/mejoras-software' && mejorasPorAclarar > 0 && (
                      <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-bold"
                        data-testid="sidebar-mejoras-badge"
                        title={mejorasPorAclarar === 1 ? '1 mejora tuya necesita una aclaración'
                          : `${mejorasPorAclarar} mejoras tuyas necesitan una aclaración`}>
                        {mejorasPorAclarar > 99 ? '99+' : mejorasPorAclarar}
                      </span>
                    )}
                    {/* Pautas de protocolo sin confirmar: rojo, como los
                        mensajes sin leer (2026-07-28). */}
                    {item.to === '/protocolos' && protocolosPendientes > 0 && (
                      <span className="ml-auto flex items-center gap-1.5" data-testid="sidebar-protocolos-badge">
                        {protocoloCartel && (
                          <span className="hidden md:inline text-[10px] font-semibold text-red-600 whitespace-nowrap"
                            title={protocoloCartel}>
                            {protocoloCartel}
                          </span>
                        )}
                        <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-red-600 text-white text-[10px] font-bold animate-pulse">
                          {protocolosPendientes}
                        </span>
                      </span>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
        ))}
      </nav>
      <div className="border-t border-border p-3">
        <div className="flex items-center gap-3 px-2 py-2">
          <Avatar className="h-9 w-9 bg-[#2E6E5E]">
            <AvatarFallback className="bg-[#2E6E5E] text-white text-xs">
              {(user?.name || '?').split(' ').slice(0, 2).map(s => s[0]).join('')}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</div>
            <div className="text-[11px] text-muted-foreground truncate uppercase tracking-wide" data-testid="sidebar-user-role">{roleLabel(user?.role)}</div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            /* Demo: al salir a propósito, no reentrar solo — deja ver los
               botones de rol para probar otro perfil. En una instalación
               real esta marca no la lee nadie. */
            onClick={async () => {
              // Con una grabación de consulta en marcha se pregunta antes, y
              // si se sigue se termina y se sube lo grabado con la sesión aún
              // viva (26/9/2026).
              if (!(await gestorGrabacion.antesDeCerrarSesion())) return;
              sessionStorage.setItem('cp_elegir_rol', '1');
              logout();
            }}
            data-testid="sidebar-logout-button"
            title="Cerrar sesión"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
        <div className="px-2 pt-1.5 pb-0.5 flex justify-center">
          <ClinicPilotBadge />
        </div>
      </div>
    </div>
  );
}

export default function AppShell() {
  const { user } = useAuth();
  // Los datos de la clínica los usa el pie de página. Estaban cogidos solo en
  // SidebarContent, que es OTRA función: aquí no existían y el pie reventaba
  // al pintar, dejando la pantalla en blanco a todo el que tuviera sesión.
  const clinicaDatos = useClinica();
  const doctor = isDoctor(user);
  const admin = isAdmin(user);
  const agente = tieneAgente(user);
  // El globo, a quien entra al chat (gestión y enfermería, 28/9/2026).
  const chatSinLeer = useChatSinLeer(tieneChat(user));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [agentQuery, setAgentQuery] = useState(null); // pregunta al Agente IA (o null)
  const location = useLocation();
  // El globo de Leads, a quien ve Leads (las mismas puertas que su ruta,
  // `SoloLeads`); abrir Leads lo pone a cero. Con campañas elegidas, el
  // servidor solo cuenta las suyas.
  const enLeads = location.pathname === '/leads' || location.pathname.startsWith('/leads/');
  const leadsNuevos = useLeadsNuevos(veLeads(user), enLeads);
  // Lo que dicen los globos del botón del menú en el móvil, para leerlo.
  const globosDelMenu = [
    chatSinLeer > 0 && `${chatSinLeer} mensajes del chat sin leer`,
    leadsNuevos > 0 && (leadsNuevos === 1 ? '1 lead nuevo' : `${leadsNuevos} leads nuevos`),
  ].filter(Boolean);
  // El buscador global navega por toda la app: los doctores no lo tienen.
  useCommandPaletteShortcut(() => !doctor && setPaletteOpen((open) => !open));

  // El título de la pestaña lleva el nombre real de la clínica. En el
  // servidor de demostraciones cada especialidad tiene el suyo propio
  // ("ClinicPilot · Clínica Dental Demo"); en una instalación real es
  // simplemente su nombre.
  useEffect(() => { applyDocumentTitle(); }, []);

  // En móvil, el gesto de "atrás" cambia la ruta sin pasar por los enlaces
  // del menú. Dos cosas quedaban colgadas y dejaban la pantalla muerta al
  // tacto (se ve bien, pero no responde):
  //  1. La Sheet del menú seguía abierta con su overlay por encima.
  //  2. Radix bloquea el scroll y los toques poniendo `pointer-events: none`
  //     en el <body> mientras hay un diálogo modal; si ese diálogo (o la
  //     propia Sheet) se DESMONTA con el cambio de ruta, su limpieza nunca
  //     llega a ejecutarse y el bloqueo se queda puesto para siempre.
  // Se cierra la Sheet y se barre cualquier bloqueo huérfano en cada
  // navegación (y al volver con el gesto atrás, vía popstate/pageshow).
  useEffect(() => {
    setMobileOpen(false);
    const limpiar = () => {
      const b = document.body;
      if (b.style.pointerEvents === 'none') b.style.pointerEvents = '';
      // Solo se libera el scroll si ya no queda ningún modal montado.
      if (!document.querySelector('[data-state="open"][role="dialog"]')) {
        if (b.style.overflow === 'hidden') b.style.overflow = '';
        b.removeAttribute('data-scroll-locked');
      }
    };
    // Tras el repintado, para no pisar la limpieza propia de Radix.
    const id = setTimeout(limpiar, 0);
    window.addEventListener('popstate', limpiar);
    window.addEventListener('pageshow', limpiar);
    return () => {
      clearTimeout(id);
      window.removeEventListener('popstate', limpiar);
      window.removeEventListener('pageshow', limpiar);
    };
  }, [location.pathname]);

  return (
    <div className="min-h-screen superficie-app">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-[280px] border-r border-border superficie-lateral">
        <SidebarContent onSearchClick={() => setAgentQuery('')} chatSinLeer={chatSinLeer} leadsNuevos={leadsNuevos} />
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between bg-white dark:bg-card border-b border-border px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="relative" data-testid="mobile-menu-button"
              aria-label={globosDelMenu.length ? `Menú (${globosDelMenu.join(', ')})` : 'Menú'}>
              <Menu className="w-5 h-5" />
              {/* En el móvil el menú está cerrado: el globo del chat asoma
                  en el botón, para no tener que abrirlo para saberlo. */}
              {chatSinLeer > 0 && (
                <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[9px] font-bold"
                  data-testid="mobile-chat-badge">
                  {chatSinLeer > 99 ? '99+' : chatSinLeer}
                </span>
              )}
              {/* Y el de los leads nuevos, abajo, para que no se tapen. */}
              {leadsNuevos > 0 && (
                <span className="absolute -bottom-0.5 -right-0.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[9px] font-bold"
                  data-testid="mobile-leads-badge">
                  {leadsNuevos > 99 ? '99+' : leadsNuevos}
                </span>
              )}
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-[280px]">
            <SidebarContent onItemClick={() => setMobileOpen(false)} onSearchClick={() => { setMobileOpen(false); setAgentQuery(''); }} chatSinLeer={chatSinLeer} leadsNuevos={leadsNuevos} />
          </SheetContent>
        </Sheet>
        <ClinicPilotLogo height={22} />
        <div className="flex items-center">
          {agente && <AvisosBell />}
          {agente && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setAgentQuery('')}
              aria-label="Preguntar al Agente"
              data-testid="mobile-search-button"
            >
              <AgenteIcono size={20} />
            </Button>
          )}
        </div>
      </header>

      <main className="lg:pl-[280px]">
        {/* El aviso amarillo de «han pedido cita por correo» que salía arriba
            al entrar se quitó (dirección, 28/9/2026: «me molesta al entrar cada
            vez»). Esas peticiones siguen en Tareas, en la campana y, las de
            los formularios web, ya como lead en Leads. */}
        <div className="px-4 sm:px-6 lg:px-8 py-5 max-w-[1500px] mx-auto">
          {/* Sonidos del día en TODA la sesión (2026-08-20) */}
          <SonidosVigilante />
          <Outlet />
        </div>
        <footer className="px-4 sm:px-6 lg:px-8 py-6 text-center text-[11px] text-muted-foreground border-t border-border mt-10">
          <div>{pieClinica(clinicaDatos)}</div>
          <div className="mt-2 inline-flex items-center gap-1.5">
            <ClinicPilotMark size={13} />
            <span>
              Hecho con{' '}
              <a
                href="https://clinicpilot.es"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold hover:text-[#13231E]"
                style={{ fontFamily: "Charter, 'Iowan Old Style', Georgia, serif" }}
              >
                Clinic<span style={{ color: '#2E6E5E' }}>Pilot</span>
              </a>
              {' '}· clinicpilot.es
            </span>
          </div>
        </footer>
      </main>

      {/* Guard global: fuerza el cambio de contraseña en el primer login */}
      <ChangePasswordGuard />

      {/* La grabación de consulta en marcha: fija en todas las pantallas. */}
      <BarraGrabacion />

      {/* Command Palette global (cmd/ctrl+K) — no para doctores. El
          anestesista la conserva para navegar, pero sin «Preguntar al
          Agente» (D7, 22/9/2026). */}
      {!doctor && (
        <CommandPalette
          open={paletteOpen}
          onOpenChange={setPaletteOpen}
          onAskAgente={agente ? (q) => { setPaletteOpen(false); setAgentQuery(q); } : undefined}
        />
      )}

      {/* Agente IA desde el buscador (2026-07-25): administración, dirección
          y enfermería, cada uno con sus permisos (D7, 22/9/2026). Es el mismo
          agente que abre el cuadro de la agenda y el del menú — una sola IA
          en toda la app (2026-07-26). */}
      {agente && (
        <AgenteAskDialog
          open={agentQuery != null}
          onOpenChange={(o) => { if (!o) setAgentQuery(null); }}
          initialQuery={agentQuery || ''}
        />
      )}
    </div>
  );
}
